import React, { useState, useEffect } from "react";
import {
  PencilIcon,
  PlusIcon,
  CheckIcon,
  XIcon,
  TrashIcon,
} from "lucide-react";
import { db } from "../../config/firebaseConfig";
import { ref, set, get } from "firebase/database";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../@/components/ui/tabs";
import { useToast } from "../../@/components/ui/use-toast";
import SortupDialog from "../../components/common/SortupDialog";
import LayoutSkeleton from "../../components/skeletons/LayoutSkeleton";

interface FAQ {
  question: string;
  answer: string;
}

interface FAQCategory {
  title: string;
  faqs: FAQ[];
}

interface FAQData {
  customers: FAQCategory;
  vendors: FAQCategory;
  general: FAQCategory;
}

const EditFaqs = () => {
  // State for storing FAQs data
  const [faqsData, setFaqsData] = useState<FAQData>({
    customers: { title: "", faqs: [] },
    vendors: { title: "", faqs: [] },
    general: { title: "", faqs: [] },
  });

  const { toast } = useToast();
  // State for tracking which FAQ is being edited
  const [editingFAQ, setEditingFAQ] = useState<{
    category: string;
    index: number;
  } | null>(null);
  // State for editing category title
  const [editingTitle, setEditingTitle] = useState<string | null>(null);
  // State for new FAQ being added
  const [isAddingNew, setIsAddingNew] = useState<string | null>(null);
  // Temporary state for editing
  const [editQuestion, setEditQuestion] = useState("");
  const [editAnswer, setEditAnswer] = useState("");
  const [editTitle, setEditTitleText] = useState("");
  // Loading state
  const [loading, setLoading] = useState(true);
  // Active tab
  const [activeTab, setActiveTab] = useState("customers");
  // Delete confirmation dialog
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [faqToDelete, setFaqToDelete] = useState<{
    category: string;
    index: number;
  } | null>(null);

  // Fetch data from Firebase on component mount
  useEffect(() => {
    const fetchFAQsData = async () => {
      try {
        const faqsRef = ref(db, "LayoutSettingData/FAQs");
        const snapshot = await get(faqsRef);

        if (snapshot.exists()) {
          setFaqsData(snapshot.val());
        } else {
          // If no data exists, initialize with default FAQs from the JSON file
          import("../../assets/faqs.json").then(async (defaultFaqs) => {
            setFaqsData(defaultFaqs.default);
            await saveToFirebase(defaultFaqs.default);
          });
        }
      } catch (error) {
        console.error("Error fetching FAQs data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchFAQsData();
  }, []);

  // Save data to Firebase
  const saveToFirebase = async (data = faqsData) => {
    try {
      const faqsRef = ref(db, "LayoutSettingData/FAQs");
      await set(faqsRef, data);
      return true;
    } catch (error) {
      console.error("Error saving FAQs data:", error);
      return false;
    }
  };

  // Handle edit FAQ button click
  const handleEditFAQ = (category: string, index: number) => {
    setEditingFAQ({ category, index });
    setEditQuestion(faqsData[category as keyof FAQData].faqs[index].question);
    setEditAnswer(faqsData[category as keyof FAQData].faqs[index].answer);
  };

  // Handle edit title button click
  const handleEditTitle = (category: string) => {
    setEditingTitle(category);
    setEditTitleText(faqsData[category as keyof FAQData].title);
  };

  // Handle save FAQ edit
  const handleSaveFAQ = async () => {
    if (!editingFAQ) return;

    const { category, index } = editingFAQ;
    const newFaqsData = { ...faqsData };
    newFaqsData[category as keyof FAQData].faqs[index] = {
      question: editQuestion,
      answer: editAnswer,
    };

    const success = await saveToFirebase(newFaqsData);
    if (success) {
      setFaqsData(newFaqsData);
      setEditingFAQ(null);
      toast({
        title: "Success",
        description: "FAQ updated successfully",
        variant: "success",
        showIcon: true,
      });
    }
  };

  // Handle save title edit
  const handleSaveTitle = async () => {
    if (!editingTitle) return;

    const newFaqsData = { ...faqsData };
    newFaqsData[editingTitle as keyof FAQData].title = editTitle;

    const success = await saveToFirebase(newFaqsData);
    if (success) {
      setFaqsData(newFaqsData);
      setEditingTitle(null);
      toast({
        title: "Success",
        description: "FAQ title updated successfully",
        variant: "success",
        showIcon: true,
      });
    }
  };

  // Handle cancel edit
  const handleCancel = () => {
    setEditingFAQ(null);
    setEditingTitle(null);
    setIsAddingNew(null);
  };

  // Handle delete FAQ
  const handleDeleteFAQ = (category: string, index: number) => {
    setFaqToDelete({ category, index });
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    if (!faqToDelete) return;

    const { category, index } = faqToDelete;
    const newFaqsData = { ...faqsData };
    newFaqsData[category as keyof FAQData].faqs = newFaqsData[
      category as keyof FAQData
    ].faqs.filter((_, i) => i !== index);

    const success = await saveToFirebase(newFaqsData);
    if (success) {
      setFaqsData(newFaqsData);
      toast({
        title: "Success",
        description: "FAQ deleted successfully",
        variant: "success",
        showIcon: true,
      });
    }
    setShowDeleteDialog(false);
    setFaqToDelete(null);
  };

  // Handle add new FAQ
  const handleAddNew = (category: string) => {
    setIsAddingNew(category);
    setEditQuestion("");
    setEditAnswer("");
  };

  // Handle save new FAQ
  const handleSaveNew = async () => {
    if (!isAddingNew) return;

    // Validate fields
    if (!editQuestion || !editAnswer) {
      toast({
        title: "Error",
        description: "Please fill in both question and answer fields",
        variant: "destructive",
        showIcon: true,
      });
      return;
    }

    const newFaqsData = { ...faqsData };
    newFaqsData[isAddingNew as keyof FAQData].faqs.push({
      question: editQuestion,
      answer: editAnswer,
    });

    const success = await saveToFirebase(newFaqsData);
    if (success) {
      setFaqsData(newFaqsData);
      setIsAddingNew(null);
      setEditQuestion("");
      setEditAnswer("");
      toast({
        title: "Success",
        description: "New FAQ added successfully",
        variant: "success",
        showIcon: true,
      });
    }
  };

  if (loading) {
    return <LayoutSkeleton />;
  }

  return (
    <div className="max-w-[1400px] mx-auto bg-white p-6">
      <div className="w-full flex flex-col font-normal text-sm space-y-6 text-black">
        <div className="flex gap-4 flex-col md:flex-row justify-between items-center w-full mb-8">
          <h1 className="font-helveticaBold text-3xl md:text-4xl text-black">
            Manage FAQs
          </h1>
        </div>

        <Tabs
          defaultValue="customers"
          value={activeTab}
          onValueChange={setActiveTab}
          className="w-full"
        >
          <div className="w-full overflow-x-auto pb-2 mb-2">
            <TabsList className="flex min-w-fit bg-transparent border-b border-gray-200">
              <TabsTrigger
                value="customers"
                className="flex-1 px-6 data-[state=active]:bg-customGreenColor data-[state=active]:text-white"
              >
                Customer FAQs
              </TabsTrigger>
              <TabsTrigger
                value="vendors"
                className="flex-1 px-6 data-[state=active]:bg-customGreenColor data-[state=active]:text-white"
              >
                Vendor FAQs
              </TabsTrigger>
              <TabsTrigger
                value="general"
                className="flex-1 px-6 data-[state=active]:bg-customGreenColor data-[state=active]:text-white"
              >
                General FAQs
              </TabsTrigger>
            </TabsList>
          </div>

          {(["customers", "vendors", "general"] as const).map((category) => (
            <TabsContent key={category} value={category} className="mt-6">
              <div className="bg-gray-50 p-6 rounded-lg shadow-sm">
                <div className="flex justify-between items-center mb-6">
                  {editingTitle === category ? (
                    <div className="w-full">
                      <input
                        type="text"
                        value={editTitle}
                        onChange={(e) => setEditTitleText(e.target.value)}
                        className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-customGreenColor focus:border-transparent"
                      />
                      <div className="flex mt-3 space-x-3">
                        <button
                          onClick={handleSaveTitle}
                          className="bg-customGreenColor hover:bg-opacity-90 transition-all text-white px-4 py-2 rounded-md flex items-center"
                        >
                          <CheckIcon className="h-4 w-4 mr-2" /> Save
                        </button>
                        <button
                          onClick={handleCancel}
                          className="bg-gray-200 hover:bg-gray-300 transition-all text-gray-700 px-4 py-2 rounded-md flex items-center"
                        >
                          <XIcon className="h-4 w-4 mr-2" /> Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col md:flex-row items-center gap-4">
                      <h2 className="font-helveticaBold text-2xl">
                        {faqsData[category].title}
                      </h2>
                      <div className="flex flex-col md:flex-row md:justify-end w-full md:w-auto gap-4 md:gap-3">
                        <button
                          className="text-white w-full md:w-auto bg-customGreenColor hover:bg-opacity-90 transition-all px-4 py-2 rounded-md flex items-center justify-center"
                          onClick={() => handleEditTitle(category)}
                        >
                          <PencilIcon className="h-5 w-5 mr-2" /> Edit
                        </button>
                        <button
                          className="text-white w-full md:w-auto bg-customGreenColor hover:bg-opacity-90 transition-all px-4 py-2 rounded-md flex items-center justify-center"
                          onClick={() => handleAddNew(category)}
                        >
                          <PlusIcon className="h-4 w-4 mr-2" /> Add FAQ
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Add New FAQ Form */}
                {isAddingNew === category && (
                  <div className="bg-white p-6 rounded-lg mb-6 shadow-sm">
                    <h3 className="font-helveticaBold text-xl mb-4">
                      Add New FAQ
                    </h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Question
                        </label>
                        <input
                          type="text"
                          value={editQuestion}
                          onChange={(e) => setEditQuestion(e.target.value)}
                          className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-customGreenColor focus:border-transparent"
                          placeholder="Enter question"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Answer
                        </label>
                        <textarea
                          value={editAnswer}
                          onChange={(e) => setEditAnswer(e.target.value)}
                          className="w-full p-3 border border-gray-300 rounded-md min-h-[150px] focus:ring-2 focus:ring-customGreenColor focus:border-transparent"
                          placeholder="Enter answer"
                        />
                      </div>
                      <div className="flex space-x-3 pt-2">
                        <button
                          onClick={handleSaveNew}
                          className="bg-customGreenColor hover:bg-opacity-90 transition-all text-white px-4 py-2 rounded-md flex items-center"
                        >
                          <CheckIcon className="h-4 w-4 mr-2" /> Save
                        </button>
                        <button
                          onClick={handleCancel}
                          className="bg-gray-200 hover:bg-gray-300 transition-all text-gray-700 px-4 py-2 rounded-md flex items-center"
                        >
                          <XIcon className="h-4 w-4 mr-2" /> Cancel
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* FAQs List */}
                <div className="space-y-4">
                  {faqsData[category].faqs.map((faq, index) => (
                    <div
                      key={index}
                      className="bg-white rounded-lg p-6 shadow-sm"
                    >
                      {editingFAQ &&
                      editingFAQ.category === category &&
                      editingFAQ.index === index ? (
                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Question
                            </label>
                            <input
                              type="text"
                              value={editQuestion}
                              onChange={(e) => setEditQuestion(e.target.value)}
                              className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-customGreenColor focus:border-transparent"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Answer
                            </label>
                            <textarea
                              value={editAnswer}
                              onChange={(e) => setEditAnswer(e.target.value)}
                              className="w-full p-3 border border-gray-300 rounded-md min-h-[150px] focus:ring-2 focus:ring-customGreenColor focus:border-transparent"
                            />
                          </div>
                          <div className="flex space-x-3 pt-2">
                            <button
                              onClick={handleSaveFAQ}
                              className="bg-customGreenColor hover:bg-opacity-90 transition-all text-white px-4 py-2 rounded-md flex items-center"
                            >
                              <CheckIcon className="h-4 w-4 mr-2" /> Save
                            </button>
                            <button
                              onClick={handleCancel}
                              className="bg-gray-200 hover:bg-gray-300 transition-all text-gray-700 px-4 py-2 rounded-md flex items-center"
                            >
                              <XIcon className="h-4 w-4 mr-2" /> Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="flex justify-between items-start">
                            <h3 className="font-helveticaBold text-xl text-black">
                              {faq.question}
                            </h3>
                            <div className="flex space-x-3 ml-4">
                              <button
                                className="text-customGreenColor hover:text-opacity-80 transition-all"
                                onClick={() => handleEditFAQ(category, index)}
                              >
                                <PencilIcon className="h-5 w-5" />
                              </button>
                              <button
                                className="text-red-500 hover:text-opacity-80 transition-all"
                                onClick={() => handleDeleteFAQ(category, index)}
                              >
                                <TrashIcon className="h-5 w-5" />
                              </button>
                            </div>
                          </div>
                          <p className="mt-3 text-gray-700 whitespace-pre-line">
                            {faq.answer}
                          </p>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </TabsContent>
          ))}
        </Tabs>
      </div>

      {/* Delete Confirmation Dialog */}
      <SortupDialog
        isOpen={showDeleteDialog}
        onClose={() => {
          setShowDeleteDialog(false);
          setFaqToDelete(null);
        }}
        header="Confirm Delete"
        footerButtons={[
          {
            name: "Delete",
            onClick: confirmDelete,
            disabled: false,
            className: "bg-customGreenColor text-white hover:bg-opacity-90",
          },
          {
            name: "Cancel",
            onClick: () => {
              setShowDeleteDialog(false);
              setFaqToDelete(null);
            },
            disabled: false,
            className: "bg-gray-200 text-gray-700 hover:bg-gray-300",
          },
        ]}
      >
        <p>
          Are you sure you want to delete this FAQ? This action cannot be
          undone.
        </p>
      </SortupDialog>
    </div>
  );
};

export default EditFaqs;
